import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Chip,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddIcon from '@mui/icons-material/Add';
import DirectBudgetGrid from './DirectBudgetGrid';
import DirectBudgetSummary from './DirectBudgetSummary';
import {
  createDirectBudget,
  completeDirectBudget,
  getDirectBudgetWorkbench,
  saveDirectBudgetLines,
  submitDirectBudget,
  validateDirectBudget,
} from './directbudget.apis';
import {
  DirectBudgetLine,
  DirectBudgetWorkbenchModel,
} from './directbudget.models';
import { PlanningPageProps, PlanningWorkbenchShell } from '../planningcore';

type State = {
  loading: boolean;
  saving: boolean;
  model?: DirectBudgetWorkbenchModel;
  rows: DirectBudgetLine[];
  error?: string;
  success?: string;
  createOpen: boolean;
  name: string;
  submitOpen: boolean;
  notes: string;
};

export default class DirectBudgetWorkbench extends React.Component<
  PlanningPageProps,
  State
> {

  state: State = {
    loading: false,
    saving: false,
    rows: [],
    createOpen: false,
    name: '',
    submitOpen: false,
    notes: '',
  };

  componentDidMount() {
    this.initialize();
  }

  componentDidUpdate(p: PlanningPageProps) {
    if (
      Number(p.plancontext?.pci || 0) !==
      Number(this.props.plancontext?.pci || 0)
    )
      this.initialize();
  }

  private pci = () => Number(this.props.plancontext?.pci || 0);

  initialize = async () => {
    const pci = this.pci();
    if (!pci)
      return this.setState({ error: 'Planning Cycle context is missing.' });
    this.setState({ loading: true, error: undefined });
    try {
      const model = await getDirectBudgetWorkbench(pci);
      this.setState({ model, rows: model.lines });
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ loading: false });
    }
  };

  create = async () => {
    this.setState({ saving: true });
    try {
      await createDirectBudget(this.pci(), this.state.name);
      this.setState({ createOpen: false, success: 'Direct Budget created.' });
      await this.initialize();
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ saving: false });
    }
  };

  save = async (rows: DirectBudgetLine[]) => {
    const h = this.state.model?.header;
    if (!h) return;
    this.setState({ saving: true });
    try {
      await saveDirectBudgetLines(h.headerid, h.stamp, rows);
      this.setState({ success: 'Direct Budget lines saved.' });
      await this.initialize();
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ saving: false });
    }
  };

  submit = async () => {
    const h = this.state.model?.header;
    if (!h) return;
    this.setState({ saving: true });
    try {
      await validateDirectBudget(h.headerid);
      await submitDirectBudget(h.headerid, h.stamp, this.state.notes);
      this.setState({ submitOpen: false, success: 'Direct Budget submitted.' });
      await this.initialize();
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ saving: false });
    }
  };

  complete = async () => {
    this.setState({ saving: true });
    try {
      const r = await completeDirectBudget(this.pci(), this.state.notes);
      const next = r?.sd?.actions?.nextpage ?? r?.actions?.nextpage;
      if (next) this.props.setPage(next);
      await this.initialize();
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ saving: false });
    }
  };
  
  render() {
    const m = this.state.model,
      h = m?.header,
      a = m?.actions;
    const actions = (
      <Stack direction="row" spacing={1}>
        {a?.canCreate && (
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => this.setState({ createOpen: true })}
          >
            Create Direct Budget
          </Button>
        )}
        {a?.canSubmit && (
          <Button
            variant="contained"
            onClick={() => this.setState({ submitOpen: true })}
          >
            Submit
          </Button>
        )}
        {a?.canComplete && (
          <Button color="success" variant="contained" onClick={this.complete}>
            Complete Direct Budget
          </Button>
        )}
      </Stack>
    );
    return (
      <PlanningWorkbenchShell
        {...this.props}
        title="Direct Budget"
        subtitle="Prepare recurrent, statutory and common-cost budgets within the active Direct Budget ceiling."
        icon={<AccountBalanceWalletIcon />}
        headerExtension={
          h ? <Chip size="small" label={h.statename} /> : undefined
        }
        summary={h ? <DirectBudgetSummary value={h} /> : undefined}
        actions={actions}
        loading={this.state.loading}
        saving={this.state.saving}
        error={this.state.error}
        success={this.state.success}
        onRefresh={this.initialize}
      >
        {!h && m && !a?.canCreate && (
          <Alert severity="warning">
            Activate a Budget Control with a Direct Budget ceiling before
            creating Direct Budget.
          </Alert>
        )}
        {h && (
          <DirectBudgetGrid
            rows={this.state.rows}
            costcenters={m?.costcenters || []}
            accounts={m?.accounts || []}
            editable={!!a?.canEdit}
            saving={this.state.saving}
            onRowsChange={(rows) => this.setState({ rows })}
            onSave={this.save}
          />
        )}
        <Dialog
          open={this.state.createOpen}
          onClose={() => this.setState({ createOpen: false })}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Create Direct Budget</DialogTitle>
          <DialogContent>
            <TextField
              sx={{ mt: 1 }}
              fullWidth
              label="Budget name"
              value={this.state.name}
              onChange={(e) => this.setState({ name: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ createOpen: false })}>
              Cancel
            </Button>
            <Button variant="contained" onClick={this.create}>
              Create
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={this.state.submitOpen}
          onClose={() => this.setState({ submitOpen: false })}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Submit Direct Budget</DialogTitle>
          <DialogContent>
            <TextField
              sx={{ mt: 1 }}
              fullWidth
              multiline
              minRows={3}
              label="Submission notes"
              value={this.state.notes}
              onChange={(e) => this.setState({ notes: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ submitOpen: false })}>
              Cancel
            </Button>
            <Button variant="contained" onClick={this.submit}>
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </PlanningWorkbenchShell>
    );
  }
}
